/**
 * Sign in — URL `/sign-in` (site root).
 */

import { useEffect, useRef, useState, useMemo } from 'react'
import { Redirect, useRouter, useLocalSearchParams, type Href } from 'expo-router'
import { useSignInAuth } from '../src/signIn/hooks/useSignInAuth'
import {
  SignInLoadingScreen,
  VerificationForm,
  PasswordResetForm,
  SignInForm,
} from '../components/signIn'
import { ErrorBoundary } from '../components/layout'
import { useAnalytics } from '../src/analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../src/analytics'
import {
  SIGN_IN_BANNER_ACCOUNT_DELETION_STARTED,
  SIGN_IN_BANNER_ACCOUNT_REMOVED,
  SIGN_IN_BANNER_DELETION_IN_PROGRESS,
  SIGN_IN_REASON_ACCOUNT_DELETED,
  SIGN_IN_REASON_ACCOUNT_DELETION_COMPLETED,
  SIGN_IN_REASON_ACCOUNT_DELETION_PENDING,
} from '../src/privacy/privacyAndData.constants'
import {
  consumePendingSignInBannerMessage,
  setPendingSignInBannerMessage,
} from '../src/signIn/helpers/signInBannerTransfer.helper'
import { useAccountDeletionGateBeforeAppEntry } from '../src/signIn/hooks/useAccountDeletionGateBeforeAppEntry'
import {
  buildPublicSignInHref,
  getSafePostAuthPath,
} from '../src/signIn/helpers/postAuthRedirect.helper'
import {
  PUBLIC_SIGN_IN_PATH,
  isPublicAuthSignupQueryValue,
} from '../src/signIn/constants/publicAuthRoutes.constants'
import { SignInFlowSafeArea } from '../src/signIn/components/SignInFlowSafeArea'
import { useCaptchaToken } from '../src/auth/turnstile/useCaptchaToken'
import { authLiveDebug } from '../src/signIn/helpers/authLiveDebug.helper'
import { useSupabaseAuth } from '../src/shared/contexts/SupabaseAuthContext'
import { getAuthUserObservabilityFields } from '../src/shared/helpers/authObservability.helper'

function SignInScreenContent() {
  const router = useRouter()
  const { track } = useAnalytics()
  const { getCaptchaToken, resetCaptcha, CaptchaWidget } = useCaptchaToken()
  const params = useLocalSearchParams<{
    reason?: string
    returnUrl?: string | string[]
    signup?: string | string[]
    invite_token?: string | string[]
  }>()
  const inviteTokenParam = useMemo(() => {
    const raw = params.invite_token
    return typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : undefined
  }, [params.invite_token])
  const postAuthPath = useMemo(() => {
    const raw = params.returnUrl
    const v = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : undefined
    return getSafePostAuthPath(v)
  }, [params.returnUrl])
  const hasHandledReason = useRef(false)
  const [signInInfoBanner, setSignInInfoBanner] = useState<string | null>(null)

  useEffect(() => {
    track(
      isPublicAuthSignupQueryValue(params.signup)
        ? ANALYTICS_EVENTS.SIGN_UP_STARTED
        : ANALYTICS_EVENTS.SIGN_IN_STARTED,
    )
  }, [track, params.signup])

  useEffect(() => {
    authLiveDebug('SignIn screen mounted', {
      inviteToken: !!inviteTokenParam,
      postAuthPath,
      signup: params.signup,
    })
  }, [inviteTokenParam, postAuthPath, params.signup])

  useEffect(() => {
    const pending = consumePendingSignInBannerMessage()
    if (pending) {
      authLiveDebug('SignIn pending banner consumed', { messageLen: pending.length })
      setSignInInfoBanner(pending)
    }
  }, [])

  useEffect(() => {
    const raw = params.reason
    const reason = Array.isArray(raw) ? raw[0] : raw
    if (!reason || hasHandledReason.current) return

    const deletionBannerByReason: Record<string, string> = {
      [SIGN_IN_REASON_ACCOUNT_DELETED]: SIGN_IN_BANNER_ACCOUNT_DELETION_STARTED,
      [SIGN_IN_REASON_ACCOUNT_DELETION_PENDING]: SIGN_IN_BANNER_DELETION_IN_PROGRESS,
      [SIGN_IN_REASON_ACCOUNT_DELETION_COMPLETED]: SIGN_IN_BANNER_ACCOUNT_REMOVED,
    }
    const deletionBanner = deletionBannerByReason[reason]
    if (deletionBanner) {
      hasHandledReason.current = true
      setPendingSignInBannerMessage(deletionBanner)
      router.replace(PUBLIC_SIGN_IN_PATH as Href)
    }
  }, [params.reason, router])

  const {
    email,
    setEmail,
    password,
    setPassword,
    loading,
    googleLoading,
    facebookLoading,
    appleLoading,
    microsoftLoading,
    forgotPasswordLoading,
    isSignUp,
    awaitingVerification,
    verificationCode,
    setVerificationCode,
    awaitingSecondFactor,
    secondFactorCode,
    setSecondFactorCode,
    secondFactorStrategy,
    awaitingPasswordReset,
    switchedToSignInBanner,
    marketingEmailOptIn,
    setMarketingEmailOptIn,
    legalTermsAccepted,
    setLegalTermsAccepted,
    isSignedIn,
    isAnonymous,
    isLoaded,
    errors,
    handleEmailAuth,
    handleVerificationCode,
    handleSecondFactor,
    handleGoogleAuth,
    handleFacebookAuth,
    handleAppleAuth,
    handleMicrosoftAuth,
    handleForgotPassword,
    handlePasswordResetSubmit,
    handleBackFromPasswordReset,
    handleBackToSignUp,
    handleBackToSignIn,
  } = useSignInAuth({ getCaptchaToken, resetCaptcha })

  // Capture whether the user was already anonymous when auth first loaded.
  // Used to distinguish "guest visiting sign-up to upgrade" from "user who just tapped
  // continue-as-guest" — the latter must redirect to /app/ even from the sign-up route.
  const wasAnonymousAtAuthLoadRef = useRef<boolean | null>(null)
  if (isLoaded && wasAnonymousAtAuthLoadRef.current === null) {
    wasAnonymousAtAuthLoadRef.current = isAnonymous
  }

  // Anonymous users who were ALREADY a guest when this page loaded are upgrading to a real
  // account via the sign-up form — do not redirect them away.
  const isAnonymousSignUpUpgrade =
    (wasAnonymousAtAuthLoadRef.current === true)
    && isPublicAuthSignupQueryValue(params.signup)

  const appEntryPhase = useAccountDeletionGateBeforeAppEntry()
  const { session } = useSupabaseAuth()

  useEffect(() => {
    authLiveDebug('SignIn auth state', {
      isLoaded,
      isSignedIn,
      isAnonymous,
      appEntryPhase,
      googleLoading,
      isAnonymousSignUpUpgrade,
      postAuthPath,
      ...getAuthUserObservabilityFields(session?.user ?? null),
    })
  }, [
    isLoaded,
    isSignedIn,
    isAnonymous,
    appEntryPhase,
    googleLoading,
    isAnonymousSignUpUpgrade,
    postAuthPath,
    session?.user,
  ])

  // Guest-first onboarding: /sign-in is now the "I already have an account" destination
  // (reached from the Welcome screen or the web landing "Sign in" CTA). First-time users go
  // Welcome → tutorial → guest mode instead, so we no longer force the tutorial before this form.
  // See specs/guest-first-onboarding/spec.md (AC-3).

  // During OTP password reset, verifyOtp briefly establishes a recovery session (isSignedIn=true).
  // Suppress the auto-redirect so the user lands on the new-password step instead of the app.
  if (isLoaded && isSignedIn && !isAnonymousSignUpUpgrade && !awaitingPasswordReset && appEntryPhase === 'verifying') {
    return (
      <SignInFlowSafeArea>
        {CaptchaWidget}
        <SignInLoadingScreen />
      </SignInFlowSafeArea>
    )
  }
  if (isLoaded && isSignedIn && !isAnonymousSignUpUpgrade && !awaitingPasswordReset && appEntryPhase === 'allow_redirect') {
    return <Redirect href={postAuthPath as Href} />
  }

  if (!isLoaded) {
    return (
      <SignInFlowSafeArea>
        {CaptchaWidget}
        <SignInLoadingScreen />
      </SignInFlowSafeArea>
    )
  }

  if (awaitingSecondFactor) {
    const getSecondFactorMessages = () => {
      switch (secondFactorStrategy) {
        case 'totp':
          return {
            title: 'Two-Factor Authentication',
            subtitle: 'Enter the code from your authenticator app',
            hint: 'Please enter the 6-digit code from your authenticator app',
          }
        case 'phone_code':
          return {
            title: 'Two-Factor Authentication',
            subtitle: 'Enter the code sent to your phone',
            hint: 'Please enter the verification code sent to your phone number',
          }
        case 'email_code':
          return {
            title: 'Two-Factor Authentication',
            subtitle: 'Enter the code sent to your email',
            hint: `Please enter the verification code sent to ${email}`,
          }
        default:
          return {
            title: 'Two-Factor Authentication',
            subtitle: 'Enter your verification code',
            hint: 'Please enter the verification code',
          }
      }
    }

    const messages = getSecondFactorMessages()

    return (
      <SignInFlowSafeArea>
        {CaptchaWidget}
        <VerificationForm
          email={email}
          verificationCode={secondFactorCode}
          setVerificationCode={setSecondFactorCode}
          loading={loading}
          onVerify={handleSecondFactor}
          onBack={handleBackToSignIn}
          title={messages.title}
          subtitle={messages.subtitle}
          hint={messages.hint}
          buttonText="Verify Code"
          backButtonText="Back to Sign In"
          error={errors.secondFactorCode}
        />
      </SignInFlowSafeArea>
    )
  }

  if (awaitingPasswordReset) {
    return (
      <SignInFlowSafeArea>
        {CaptchaWidget}
        <PasswordResetForm
          email={email}
          loading={loading}
          error={errors.passwordReset}
          onSubmit={handlePasswordResetSubmit}
          onResend={handleForgotPassword}
          resendLoading={forgotPasswordLoading}
          onBack={handleBackFromPasswordReset}
        />
      </SignInFlowSafeArea>
    )
  }

  if (awaitingVerification) {
    return (
      <SignInFlowSafeArea>
        {CaptchaWidget}
        <VerificationForm
          email={email}
          verificationCode={verificationCode}
          setVerificationCode={setVerificationCode}
          loading={loading}
          onVerify={handleVerificationCode}
          onBack={handleBackToSignUp}
          confirmationMode="code"
          error={errors.verificationCode}
        />
      </SignInFlowSafeArea>
    )
  }

  return (
    <SignInFlowSafeArea>
      {CaptchaWidget}
      <SignInForm
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        loading={loading}
        googleLoading={googleLoading}
        facebookLoading={facebookLoading}
        appleLoading={appleLoading}
        microsoftLoading={microsoftLoading}
        isSignUp={isSignUp}
        legalTermsAccepted={legalTermsAccepted}
        onToggleLegalTerms={() => setLegalTermsAccepted(!legalTermsAccepted)}
        onOpenTerms={() => router.push('/terms' as Href)}
        onOpenPrivacy={() => router.push('/privacy' as Href)}
        marketingEmailOptIn={marketingEmailOptIn}
        onToggleMarketingEmailOptIn={() => setMarketingEmailOptIn(!marketingEmailOptIn)}
        onSubmit={handleEmailAuth}
        onToggleMode={() =>
          router.push(
            (isSignUp
              ? buildPublicSignInHref({
                  returnUrl: params.returnUrl,
                  invite_token: inviteTokenParam,
                })
              : buildPublicSignInHref({
                  signup: true,
                  returnUrl: params.returnUrl,
                  invite_token: inviteTokenParam,
                })) as Href,
          )
        }
        onGoogleAuth={handleGoogleAuth}
        onFacebookAuth={handleFacebookAuth}
        onAppleAuth={handleAppleAuth}
        onMicrosoftAuth={handleMicrosoftAuth}
        onForgotPassword={handleForgotPassword}
        forgotPasswordLoading={forgotPasswordLoading}
        errors={{ email: errors.email, password: errors.password, terms: errors.terms }}
        infoBannerText={signInInfoBanner ?? switchedToSignInBanner}
      />
    </SignInFlowSafeArea>
  )
}

export default function SignInScreen() {
  return (
    <ErrorBoundary>
      <SignInScreenContent />
    </ErrorBoundary>
  )
}
